import { Injectable } from "@angular/core";
import { ListMetadata } from "app/models/list-metadata.model";
import { HttpClientService } from "app/services/httpClient/httpclient.service";
import { Observable } from "rxjs";
import { of } from "rxjs/observable/of";
import { MessageService } from "primeng/primeng";
import { TranslateService } from "@ngx-translate/core";
import { Basket, CreateBasketPayload, ProductItemComplete } from "./baskets.model";
import { HttpParams } from "@angular/common/http";


@Injectable()
export class BasketService {
  // ========================
  // URL
  // ========================
  private readonly basketCreationUrl = 'koncentro-promo/basket';
  private readonly basketCompleteUrl = 'koncentro-promo/basket/complete';
  private readonly campaignBaseUrl = 'koncentro-promo/campaign';
  private readonly productItemBaseUrl = 'koncentro-common/product-item';
  private readonly basketCommonBaseUrl = 'koncentro-common/basket';
  private readonly duplicateBasketUrl = 'koncentro-promo/basket/duplicateBasket';
  private readonly deleteBasketUrl = 'koncentro-promo/campaign/message-instance/basket';
  private readonly recoverBasketUrl = 'koncentro-promo/campaign/message-instance/basket';
  private readonly basketImportCsvBaseUrl = 'k7o/gui-shim/promotion/basket/import_basket';

  constructor(
    private http: HttpClientService,
    private srvNotify: MessageService,
    private translate: TranslateService
  ) { }

  // ========================
  // BASKET LIST
  // ========================
  list(
    offset = null,
    limit = 9999,
    search?: Array<{ key: string; value: string }>,
    globalSearch?: string,
    sort?: string,
    sort_dir?: number
  ): Observable<{ data: any[]; metadata: ListMetadata }> {
    const params = HttpClientService.buildListParams(
      offset,
      limit,
      search,
      globalSearch,
      sort,
      sort_dir
    );

    return this.http.getPaged(
      `${this.basketCompleteUrl}?${params.toString()}`
    );
  }

  // ========================
  // PRODUCT ITEM
  // ========================
  getProductItemCompleteOnlyBarcode(
    offset = null,
    limit = null,
    search?: Array<{ key: string; value: string }>,
    globalSearch?: string,
    sort?: string,
    sort_dir?: number
  ): Observable<{ data: ProductItemComplete[] }> {
    const params = HttpClientService.buildListParams(
      offset,
      limit,
      search,
      globalSearch,
      sort,
      sort_dir
    );

    return this.http.getPaged(
      `${this.productItemBaseUrl}/complete?${params.toString()}&only_barcode=true`
    );
  }

  // TODO da togliere
  getProductItemComplete(
    offset = null,
    limit = null,
    search?: Array<{ key: string; value: string }>,
    globalSearch?: string,
    sort?: string,
    sort_dir?: number
  ): Observable<{ data: ProductItemComplete[] }> {
    const params = HttpClientService.buildListParams(
      offset,
      limit,
      search,
      globalSearch,
      sort,
      sort_dir
    );

    return this.http.getPaged(
      `${this.productItemBaseUrl}/complete?${params.toString()}`
    );
  }

  getProductItemNotInBasket(
    notBasketId: number
  ): Observable<{ data: ProductItemComplete[] }> {
    const params = new HttpParams()
      .append('basket_id', notBasketId.toString());

    return this.http.getPaged(
      `${this.productItemBaseUrl}/complete-not-in-basket?` + params.toString()
    );
  }
  //todo

  getBasket(
    basketId: number
  ): Observable<{ data: Basket }> {
    const params = new HttpParams()
      .append('basket_id', basketId.toString());

    return this.http.getPaged(
      `${this.basketCreationUrl}/` + basketId.toString()
    );
  }


  /**
   * Lista con almeno un filtro obbligatorio (param1..param5)
   */
  list2(
    offset = null,
    limit = null,
    search?: Array<{ key: string; value: string }>,
    globalSearch?: string,
    sort?: string,
    sort_dir?: number
  ): Observable<{ data: Array<ProductItemComplete> }> {

    let indexP1 = -1;
    let indexP2 = -1;
    let indexP3 = -1;
    let indexP4 = -1;
    let indexP5 = -1;

    if (search && Array.isArray(search)) {
      indexP1 = search.findIndex(f => f.key === 'param1');
      indexP2 = search.findIndex(f => f.key === 'param2');
      indexP3 = search.findIndex(f => f.key === 'param3');
      indexP4 = search.findIndex(f => f.key === 'param4');
      indexP5 = search.findIndex(f => f.key === 'param5');
    }

    if (
      indexP1 === -1 &&
      indexP2 === -1 &&
      indexP3 === -1 &&
      indexP4 === -1 &&
      indexP5 === -1
    ) {
      this.srvNotify.add({
        severity: 'error',
        summary: this.translate.instant('common.warning'),
        detail: this.translate.instant('common.addFilterTable')
      });
      return;
    }

    const params = HttpClientService.buildListParams(
      offset,
      limit,
      search,
      globalSearch,
      sort,
      sort_dir
    );

    const queryString = params.toString()
      ? '?' + params.toString().replace(/\+/gi, '%2B')
      : '';

    return this.http.getPaged(
      '/koncentro-common/product-item/complete' + queryString
    );
  }


  // ========================
  // BASKET CRUD
  // ========================
  saveBasket(payload: any): Observable<any> {
    return this.http.post(`${this.campaignBaseUrl}/basket`, payload);
  }

  editBasket(payload: any, idBasket: number): Observable<any> {
    const params = new HttpParams()
      .append('basketId', idBasket.toString());

    const queryString = params.toString()
      ? '?' + params.toString()
      : '';

    return this.http.put(
      `${this.basketCreationUrl}/updateBasket${queryString}`,
      payload
    );
  }


  createNewBasket(payload: CreateBasketPayload): Observable<any> {
    return this.http.post(
      `${this.basketCreationUrl}/createBasket`,
      payload
    );
  }

  deleteBasket(idBasket: number): Observable<any> {
    return this.http.delete(
      `${this.deleteBasketUrl}/${idBasket}`
    );
  }

  recoverBasket(idBasket: number, payload: CreateBasketPayload): Observable<any> {
    return this.http.put(
      `${this.recoverBasketUrl}/${idBasket}`,
      payload
    );
  }
  // ========================
  // BASKET CONTENT
  // ========================
  getBasketProduct(idBasket: number): Observable<ProductItemComplete[]> {

    const params = new HttpParams()
      .set('basket_id', idBasket.toString())
      .set('basket_detail_flg_state', '0');

    const queryString = params.toString()
      ? '?' + params.toString()
      : '';

    return this.http.get(
      `${this.productItemBaseUrl}/complete${queryString}`
    );
  }

  getArticlesFromBasket_old(
    basketId: string
  ): Observable<{ data: any[]; metadata: ListMetadata }> {
    const params = new HttpParams().set('basket_id', basketId);

    return this.http.getPaged(
      `${this.productItemBaseUrl}/complete?${params.toString()}`
    );
  }

  getArticlesFromBasket(
    basketId: string
  ): Observable<{ data: any[]; metadata: ListMetadata }> {

    const params = new HttpParams()
      .set('basket_id', basketId)
      .set('limit', '9999');

    return this.http.getPaged(
      `${this.productItemBaseUrl}/complete?${params.toString()}`
    );
  }

  addProductToBasket(
    basketId: number,
    data: ProductItemComplete[]
  ): Observable<any> {
    const url = `koncentro-promo/basket/${basketId}/details`;
    const product_item_ids_list = data.map(item => item.id);

    return this.http.post(url, { product_item_ids_list });
  }

  removeProducts(
    basketId: number,
    basketDetailIds: ProductItemComplete[]
  ): Observable<any> {
    return this.http.post(
      '/koncentro-promo/basket/removeFromBasket',
      {
        basketId,
        basketDetailIds: basketDetailIds.map(item => item.basket_detail_id)
      }
    );
  }

  // ========================
  // DUPLICATE
  // ========================
  duplicateBasket(payload: {
    id: number;
    code: string;
    name: string;
    description: string;
  }): Observable<any> {
    return this.http.post(this.duplicateBasketUrl, payload);
  }

  // ========================
  // ACTIONS & PROMOS
  // ========================
  getActiveActions(idBasket: number): Observable<any> {
    return this.http.get(
      `${this.basketCreationUrl}/active-actions?basket_id=${idBasket}`
    );
  }

 

  // ========================
  // IMPORT / EMPTY
  // ========================
  importFromCsv(idBasket: number, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('basketDetailCsv', file);
    return this.http.upload(
      `${this.basketImportCsvBaseUrl}/${idBasket}`,
      formData
    );
  }

  emptyBasket(idBasket: number): Observable<any> {
    return this.http.post(
      `${this.basketCreationUrl}/${idBasket}/empty`,
      null
    );
  }


   private readonly messageInstanceBase =
    'koncentro-promo/campaign/message-instance';

  getActivePromos(idBasket: string): Observable<any> {
    const url =
      `${this.messageInstanceBase}/active-promos?basket_code=${idBasket}`;

    return this.http.get(url);
  }

  getBasketActions(
    basketId: string,
    activeActionStatus: string
  ): Observable<{ data: Array<any>; metadata: ListMetadata }> {

    let url =
      `${this.messageInstanceBase}/active-actions?basket_id=${basketId}`;

    if (activeActionStatus === '1') {
      url += `&active=${activeActionStatus}`;
    } else {
      url += `&all=true`;
    }

    return this.http.getPaged(url);
  }


}





// WEBPACK FOOTER //
// ./src/app/views/baskets/basket.service.ts